class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
    console.log('API Base URL:', this.baseURL);
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and ensure the backend server is running.');
      }
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Authentication endpoints
  async authenticateEmployee(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // Employee endpoints
  async getEmployees() {
    return this.request('/api/employees');
  }

  async createEmployee(employeeData) {
    return this.request('/api/employees', {
      method: 'POST',
      body: JSON.stringify(employeeData),
    });
  }

  async updateEmployee(employeeId, employeeData) {
    return this.request(`/api/employees/${employeeId}`, {
      method: 'PUT',
      body: JSON.stringify(employeeData),
    });
  }

  async deleteEmployee(employeeId) {
    return this.request(`/api/employees/${employeeId}`, {
      method: 'DELETE',
    });
  }

  // Project endpoints
  async getProjects() {
    return this.request('/api/projects');
  }

  async getEmployeeProjects(employeeId) {
    return this.request(`/api/projects/employee/${employeeId}`);
  }

  async createProject(projectData) {
    return this.request('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  async updateProject(projectId, projectData) {
    return this.request(`/api/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  async deleteProject(projectId) {
    return this.request(`/api/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  // Timesheet endpoints
  async submitTimesheet(timesheetData) {
    return this.request('/api/timesheets', {
      method: 'POST',
      body: JSON.stringify(timesheetData),
    });
  }

  async getTimesheets(employeeId, startDate, endDate) {
    const params = new URLSearchParams();
    if (employeeId) params.append('employeeId', employeeId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    return this.request(`/api/timesheets${queryString ? '?' + queryString : ''}`);
  }

  async getAllTimesheets(startDate, endDate) {
    return this.getTimesheets(null, startDate, endDate);
  }

  async updateTimesheet(timesheetId, timesheetData) {
    return this.request(`/api/timesheets/${timesheetId}`, {
      method: 'PUT',
      body: JSON.stringify(timesheetData),
    });
  }

  async deleteTimesheet(timesheetId) {
    return this.request(`/api/timesheets/${timesheetId}`, {
      method: 'DELETE',
    });
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
